import React, { useState, useCallback } from "react";
import { Button, ButtonGroup, Spinner } from "reactstrap";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import numeral from "numeral";
import "numeral/locales";
import useSponsorValueBreakdown from "../query/hooks/useSponsorValueBreakdown";

const SponsorValueBreakdown = ({ selectedCurrency, onFiltersChange }) => {
  const { t } = useTranslation();
  const [activeMode, setActiveMode] = useState("channel");
  const [drilldown, setDrilldown] = useState(null);
  const [drilldownTitle, setDrilldownTitle] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState({
    channels: [],
    markets: [],
    rights: [],
  });

  const { data, isFetching, isPlaceholderData } = useSponsorValueBreakdown({
    mode: activeMode,
    drilldown: drilldown,
  });

  // Show loading state when:
  // 1. Fetching with placeholder data (initial load or new query with no cache)
  // 2. Fetching and the data doesn't match the current request (stale data from previous mode/drilldown)
  const dataMatchesRequest = data?.mode === activeMode || (drilldown && data?.drilldown === drilldown);
  const isLoading = isFetching && (isPlaceholderData || !dataMatchesRequest);

  const handleModeChange = useCallback((mode) => {
    setActiveMode(mode);
    setDrilldown(null);
    setDrilldownTitle(null);
    // Don't clear filters when changing mode - allow cross-mode filtering
  }, []);

  const handleBarClick = useCallback((item) => {
    if (item.drillable && activeMode === "channel") {
      setDrilldown(item.id);
      setDrilldownTitle(`${item.name} Breakdown`);
    }
  }, [activeMode]);

  const handleBackToChannel = useCallback(() => {
    setDrilldown(null);
    setDrilldownTitle(null);
  }, []);

  const handleFilterToggle = useCallback((type, id, name) => {
    setSelectedFilters((prev) => {
      const currentList = prev[type];
      const isSelected = currentList.some(item => item.id === id);

      let newList;
      if (isSelected) {
        newList = currentList.filter(item => item.id !== id);
      } else {
        newList = [...currentList, { id, name }];
      }

      const newFilters = {
        ...prev,
        [type]: newList,
      };

      // Notify parent component of filter changes
      if (onFiltersChange) {
        onFiltersChange({
          channels: newFilters.channels.map(f => f.id),
          markets: newFilters.markets.map(f => f.id),
          rights: newFilters.rights.map(f => f.id),
        });
      }

      return newFilters;
    });
  }, [onFiltersChange]);

  const handleClearFilters = useCallback(() => {
    setSelectedFilters({
      channels: [],
      markets: [],
      rights: [],
    });
    if (onFiltersChange) {
      onFiltersChange({
        channels: [],
        markets: [],
        rights: [],
      });
    }
  }, [onFiltersChange]);

  const isFilterSelected = useCallback((type, id) => {
    return selectedFilters[type].some(item => item.id === id);
  }, [selectedFilters]);

  const getFilterType = useCallback(() => {
    switch (activeMode) {
      case 'channel':
        return 'channels';
      case 'market':
        return 'markets';
      case 'rights':
        return 'rights';
      default:
        return null;
    }
  }, [activeMode]);

  const hasActiveFilters = selectedFilters.channels.length > 0 ||
                          selectedFilters.markets.length > 0 ||
                          selectedFilters.rights.length > 0;

  // Check if "earned" is the only selected channel (market filtering not supported for earned)
  const isEarnedOnlySelected = selectedFilters.channels.length === 1 &&
                               selectedFilters.channels[0].id === 'earned';

  // Clear market filters when earned channel is selected
  React.useEffect(() => {
    if (isEarnedOnlySelected && selectedFilters.markets.length > 0) {
      setSelectedFilters(prev => ({
        ...prev,
        markets: [],
      }));
      if (onFiltersChange) {
        onFiltersChange({
          channels: selectedFilters.channels.map(f => f.id),
          markets: [],
          rights: selectedFilters.rights.map(f => f.id),
        });
      }
    }
  }, [isEarnedOnlySelected, selectedFilters.channels, selectedFilters.markets.length, selectedFilters.rights, onFiltersChange]);

  // Format currency with shorthand and symbol
  const formatCurrency = useCallback((value) => {
    if (!value || !selectedCurrency?.value) return "0";

    const convertedValue = Math.round(value * selectedCurrency.value);
    const locale = selectedCurrency.locale || "no";

    numeral.locale(locale);

    const currencySymbols = {
      NOK: "kr",
      SEK: "kr",
      EUR: "\u20AC",
      USD: "$",
      GBP: "\u00A3",
    };

    const symbol = currencySymbols[selectedCurrency.currency] || selectedCurrency.currency;
    const formattedValue = numeral(convertedValue).format("0.[0]a");

    if (["EUR", "USD", "GBP"].includes(selectedCurrency.currency)) {
      return `${symbol}${formattedValue}`;
    }

    return `${formattedValue} ${symbol}`;
  }, [selectedCurrency]);

  // Calculate max value for bar width scaling
  const maxValue = data?.items?.length > 0
    ? Math.max(...data.items.map(item => item.value))
    : 0;

  const isDrilldown = drilldown !== null;
  const displayTitle = isDrilldown && data?.title
    ? `${data.title} - ${formatCurrency(data.total)}`
    : null;

  return (
    <div style={{ position: "relative", minHeight: "300px" }}>
      {/* Header with title and mode buttons */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h6 className="text-uppercase mb-0" style={{ color: "var(--text-secondary)", letterSpacing: "1px" }}>
          {t("sponsor_value_breakdown")}
        </h6>

        {!isDrilldown ? (
          <ButtonGroup size="sm">
            <Button
              color={activeMode === "channel" ? "primary" : "secondary"}
              outline={activeMode !== "channel"}
              onClick={() => handleModeChange("channel")}
              style={{ borderRadius: "20px 0 0 20px" }}
            >
              {t("by_channel")}
            </Button>
            <Button
              color={activeMode === "market" ? "primary" : "secondary"}
              outline={activeMode !== "market"}
              onClick={() => handleModeChange("market")}
              disabled={isEarnedOnlySelected}
              title={isEarnedOnlySelected ? t("market_filter_not_available_for_earned") : ""}
            >
              {t("by_market")}
            </Button>
            <Button
              color={activeMode === "rights" ? "primary" : "secondary"}
              outline={activeMode !== "rights"}
              onClick={() => handleModeChange("rights")}
              style={{ borderRadius: "0 20px 20px 0" }}
            >
              {t("by_rights")}
            </Button>
          </ButtonGroup>
        ) : (
          <Button
            color="primary"
            size="sm"
            onClick={handleBackToChannel}
            style={{ borderRadius: "20px" }}
          >
            <i className="ti-arrow-left mr-2"></i>
            {t("back_to_channel")}
          </Button>
        )}
      </div>

      {/* Active filters display and clear button */}
      {hasActiveFilters && (
        <div className="d-flex align-items-center mb-3" style={{ gap: "8px", flexWrap: "wrap" }}>
          {selectedFilters.channels.map((filter) => (
            <span
              key={`channel-${filter.id}`}
              className="badge badge-primary"
              style={{ fontSize: "12px", padding: "6px 10px", cursor: "pointer" }}
              onClick={() => handleFilterToggle('channels', filter.id, filter.name)}
            >
              {filter.name} <i className="mdi mdi-close ml-1"></i>
            </span>
          ))}
          {selectedFilters.markets.map((filter) => (
            <span
              key={`market-${filter.id}`}
              className="badge badge-primary"
              style={{ fontSize: "12px", padding: "6px 10px", cursor: "pointer" }}
              onClick={() => handleFilterToggle('markets', filter.id, filter.name)}
            >
              {filter.name} <i className="mdi mdi-close ml-1"></i>
            </span>
          ))}
          {selectedFilters.rights.map((filter) => (
            <span
              key={`rights-${filter.id}`}
              className="badge badge-primary"
              style={{ fontSize: "12px", padding: "6px 10px", cursor: "pointer" }}
              onClick={() => handleFilterToggle('rights', filter.id, filter.name)}
            >
              {filter.name} <i className="mdi mdi-close ml-1"></i>
            </span>
          ))}
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={handleClearFilters}
            style={{ fontSize: "11px", padding: "4px 8px" }}
          >
            {t("clear_all_filters")}
          </button>
        </div>
      )}

      {/* Drilldown title */}
      {displayTitle && (
        <h5 className="mb-4" style={{ color: "#379f8b" }}>
          {displayTitle}
        </h5>
      )}

      {/* Loading spinner */}
      {isLoading && (
        <div style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 10,
        }}>
          <Spinner color="primary" />
        </div>
      )}

      {/* Horizontal bar chart */}
      <div style={{ opacity: isLoading ? 0.5 : 1, transition: "opacity 0.3s" }}>
        {data?.items?.map((item, index) => {
          // Disable market checkboxes when earned channel is selected
          const isCheckboxDisabled = activeMode === 'market' && isEarnedOnlySelected;

          return (
            <HorizontalBar
              key={item.id}
              item={item}
              maxValue={maxValue}
              formatCurrency={formatCurrency}
              onClick={handleBarClick}
              index={index}
              showCheckbox={!isDrilldown && !isCheckboxDisabled}
              isSelected={isFilterSelected(getFilterType(), item.id)}
              onCheckboxChange={() => handleFilterToggle(getFilterType(), item.id, item.name)}
            />
          );
        })}

        {data?.items?.length === 0 && !isLoading && (
          <div className="text-center py-5" style={{ color: "var(--text-secondary)" }}>
            {t("no_data_available")}
          </div>
        )}
      </div>

      {/* Click hint */}
      {activeMode === "channel" && !isDrilldown && data?.items?.some(item => item.drillable) && (
        <div className="text-center mt-4" style={{ color: "var(--text-secondary)", fontSize: "12px" }}>
          <i className="ti-hand-point-up mr-2"></i>
          {t("click_bar_to_drill_down")}
        </div>
      )}
    </div>
  );
};

// Horizontal bar component
const HorizontalBar = ({
  item,
  maxValue,
  formatCurrency,
  onClick,
  index,
  showCheckbox = false,
  isSelected = false,
  onCheckboxChange
}) => {
  const widthPercentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0;
  const isClickable = item.drillable;
  // Show value outside bar if bar is too narrow (less than 15% width)
  const showValueOutside = widthPercentage < 15;

  const handleRowClick = (e) => {
    // Don't trigger row click if clicking on checkbox
    if (e.target.type === 'checkbox' || e.target.closest('.filter-checkbox')) {
      return;
    }
    if (isClickable) {
      onClick(item);
    }
  };

  return (
    <div
      className="d-flex align-items-center mb-3"
      style={{
        cursor: isClickable ? "pointer" : "default",
      }}
      onClick={handleRowClick}
    >
      {/* Checkbox */}
      {showCheckbox && (
        <div
          className="filter-checkbox"
          style={{ marginRight: "10px" }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            checked={isSelected}
            onChange={onCheckboxChange}
            style={{
              width: "16px",
              height: "16px",
              cursor: "pointer",
              accentColor: item.color
            }}
          />
        </div>
      )}

      {/* Label */}
      <div
        style={{
          width: "100px",
          minWidth: "100px",
          paddingRight: "15px",
          color: "var(--text-primary)",
          fontSize: "14px",
          textAlign: "left",
        }}
      >
        {item.name}
      </div>

      {/* Bar container */}
      <div
        style={{
          flex: 1,
          height: "36px",
          backgroundColor: "var(--bg-secondary)",
          borderRadius: "4px",
          overflow: "visible",
          position: "relative",
          display: "flex",
          alignItems: "center",
        }}
      >
        {/* Bar fill */}
        <div
          style={{
            width: `${Math.max(widthPercentage, 1)}%`,
            height: "100%",
            backgroundColor: item.color,
            borderRadius: "4px",
            transition: "width 0.5s ease-out",
            transitionDelay: `${index * 100}ms`,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            paddingRight: showValueOutside ? "0" : "10px",
          }}
        >
          {/* Value inside bar when bar is wide enough */}
          {!showValueOutside && (
            <span
              style={{
                color: "#1a1a2e",
                fontWeight: "600",
                fontSize: "13px",
                whiteSpace: "nowrap",
              }}
            >
              {formatCurrency(item.value)}
            </span>
          )}
        </div>
        {/* Value outside bar when bar is too narrow */}
        {showValueOutside && (
          <span
            style={{
              color: "var(--text-primary)",
              fontWeight: "600",
              fontSize: "13px",
              whiteSpace: "nowrap",
              marginLeft: "8px",
            }}
          >
            {formatCurrency(item.value)}
          </span>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  selectedCurrency: state.currency.selectedCurrency,
});

export default connect(mapStateToProps)(SponsorValueBreakdown);
